import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Plus } from 'lucide-react';
import { aiVisibilityService } from '../../services/aiVisibilityService';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import HeroDemo from './components/HeroDemo';
import CheckForm from './components/CheckForm';
import CheckProgress from './components/CheckProgress';
import CheckResult from './components/CheckResult';
import LegalLinks from '../../components/LegalLinks';
import { FULL_REPORT_PRICE, FULL_REPORT_TAX_NOTE } from '../../config/pricing';
import './landing.css';

const MARQUEE_QUESTIONS = [
  '¿Mejor clínica dental en Getafe?',
  'Fontanero urgente en Móstoles',
  '¿Dónde comer arroz en Valencia?',
  'Peluquería para rizos en Sevilla',
  '¿Qué gestoría me recomiendas en Bilbao?',
  'Taller de bicis cerca de Gràcia',
  '¿Buen fisio en Zaragoza?',
  'Pan de masa madre en Malasaña'
];

const STEPS = [
  {
    title: 'Preguntamos como tus clientes',
    body: 'Formulamos la pregunta que haría alguien que busca lo que tú vendes, en tu ciudad.'
  },
  {
    title: 'La IA responde con la web en tiempo real',
    body: 'Usamos un modelo con búsqueda web, como los asistentes que ya usa la gente para decidir dónde ir.'
  },
  {
    title: 'Te decimos quién gana',
    body: 'Si apareces, en qué puesto, y qué negocios se llevan la recomendación cuando tú no estás.'
  }
];

const FREE_ITEMS = ['Una pregunta, formulada como la haría un cliente', 'Si la IA te nombra o no', 'Qué competidores recomienda'];

const FULL_ITEMS = [
  '12 preguntas como las que de verdad hacen tus clientes',
  'En qué porcentaje de respuestas apareces tú y cada competidor',
  'Por qué la IA los elige a ellos, con pruebas',
  'Tu plan GEO paso a paso, sin tecnicismos',
  'Hecho para ti: textos listos para tu web, tu ficha de Google y para pedir reseñas',
  'Pago único: sin suscripción ni permanencia'
];

const FAQS = [
  {
    q: '¿Qué IA usáis?',
    a: 'Hacemos la pregunta a un modelo de IA con búsqueda web en tiempo real, igual que funcionan los asistentes que usa la gente. Cada asistente (ChatGPT, Gemini, Perplexity…) tiene su propia forma de responder, así que el resultado es una señal fiable de cómo te ve la IA, no una copia exacta de lo que verá cada persona.'
  },
  {
    q: '¿Por qué la IA recomienda a unos negocios y no a otros?',
    a: 'Porque se basa en lo que encuentra sobre ti en internet: reseñas, tu ficha de Google, menciones en directorios y medios, y lo que dice tu propia web. Si esa información es escasa o confusa, recomienda a quien la tenga más clara.'
  },
  {
    q: '¿Qué es el GEO? ¿No es lo mismo que el SEO?',
    a: 'El SEO trabaja para que salgas en la lista de resultados de Google. El GEO (optimización para motores generativos) trabaja para que ChatGPT, Gemini o Perplexity te nombren cuando alguien les pide una recomendación. No van siempre juntos: puedes salir bien en Google y que la IA no te mencione, o al revés. Nosotros nos centramos solo en lo segundo.'
  },
  {
    q: '¿Es para un negocio pequeño como el mío?',
    a: 'Está hecho justo para eso: bares, peluquerías, talleres, clínicas, tiendas, gestorías… La mayoría de herramientas de GEO están pensadas para grandes marcas y agencias, con suscripciones mensuales y paneles llenos de datos. Aquí pagas una vez y recibes qué cambiar y los textos ya escritos para hacerlo.'
  },
  {
    q: '¿La comprobación gratuita es de verdad gratis?',
    a: 'Sí. Sin registro y sin tarjeta. Solo pagas si quieres el informe completo.'
  }
];

const scrollBehavior = (reduceMotion) => (reduceMotion ? 'auto' : 'smooth');

const CancelledNote = ({ children }) => (
  <p role="status" className="mb-10 max-w-3xl rounded-2xl border border-[var(--lp-line)] px-5 py-4 text-[var(--lp-muted)]">
    <span className="font-semibold text-[var(--lp-text)]">Has cancelado el pago.</span> No se te ha cobrado nada.
    {children && <> {children}</>}
  </p>
);

// The last check is kept for this tab, so that cancelling Stripe's payment
// page (which sends the customer to /?pago=cancelado) brings back the result
// instead of an empty form.
const LAST_CHECK_KEY = 'webspector-last-check';

const saveLastCheck = (input, result) => {
  try {
    sessionStorage.setItem(LAST_CHECK_KEY, JSON.stringify({ input, result }));
  } catch {
    // storage unavailable: nothing to restore later
  }
};

const readCancelledPayment = () => {
  if (typeof window === 'undefined' || new URLSearchParams(window.location.search).get('pago') !== 'cancelado') {
    return null;
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem(LAST_CHECK_KEY) || 'null');
    return saved?.input && saved?.result ? saved : { input: null, result: null };
  } catch {
    return { input: null, result: null };
  }
};

const Landing = () => {
  const reduceMotion = usePrefersReducedMotion();
  const navigate = useNavigate();
  const [cancelled] = useState(readCancelledPayment);
  const [status, setStatus] = useState(cancelled?.result ? 'done' : 'idle'); // idle | loading | done | error
  const [input, setInput] = useState(cancelled?.input ?? null);
  const [result, setResult] = useState(cancelled?.result ?? null);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentCancelled, setPaymentCancelled] = useState(Boolean(cancelled));
  const resultRef = useRef(null);

  // Drop ?pago=cancelado from the address once read.
  useEffect(() => {
    if (cancelled) navigate('/', { replace: true });
  }, [cancelled, navigate]);

  useEffect(() => {
    if (status !== 'idle') {
      resultRef.current?.scrollIntoView({ behavior: scrollBehavior(reduceMotion), block: 'start' });
    }
  }, [status, reduceMotion]);

  const handleCheck = async (values) => {
    setInput(values);
    setResult(null);
    setErrorMessage('');
    setPaymentCancelled(false);
    setStatus('loading');

    const response = await aiVisibilityService.runCheck(values);

    if (response.success) {
      setResult(response.data);
      setStatus('done');
      saveLastCheck(values, response.data);
    } else {
      setErrorMessage(response.message);
      setStatus('error');
    }
  };

  const handleReset = () => {
    setStatus('idle');
    setResult(null);
    setPaymentCancelled(false);
    document.getElementById('comprobar')?.scrollIntoView({ behavior: scrollBehavior(reduceMotion) });
    document.getElementById('businessName')?.focus({ preventScroll: true });
  };

  return (
    <div className="lp">
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-4 py-6 sm:px-6 lg:px-8">
        <Link to="/" className="lp-display text-2xl">
          webspector<span className="text-[var(--lp-lime)]">.</span>
        </Link>
        <nav aria-label="Principal" className="flex items-center gap-6 text-sm">
          <a href="#como-funciona" className="hidden text-[var(--lp-muted)] transition-colors hover:text-[var(--lp-text)] sm:inline">
            Cómo funciona
          </a>
          <a href="#precio" className="hidden text-[var(--lp-muted)] transition-colors hover:text-[var(--lp-text)] sm:inline">
            Precio
          </a>
          <Link
            to="/informe/ejemplo"
            className="rounded-full border border-[var(--lp-line)] px-4 py-2 font-semibold transition-colors hover:border-[var(--lp-text)]"
          >
            Ver ejemplo
          </Link>
        </nav>
      </header>

      <main>
        {paymentCancelled && !result && (
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <CancelledNote>Haz la comprobación de nuevo para volver al pago.</CancelledNote>
          </div>
        )}

        {/* Hero */}
        <section className="relative">
          <div aria-hidden="true" className="lp-grid-bg pointer-events-none absolute inset-0" />
          <div className="relative mx-auto grid max-w-6xl gap-16 px-4 pb-24 pt-8 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:pb-32 lg:pt-14">
            <div className="lg:col-span-7">
              <p className="lp-kicker">GEO para negocios locales</p>
              <h1 className="lp-display mt-6 text-[clamp(3.2rem,7vw,5.9rem)] leading-[0.96]">
                Tus clientes ya no buscan.
                <br />
                <span className="lp-serif text-[1.12em] text-[var(--lp-lime)]">Preguntan.</span>
              </h1>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-[var(--lp-muted)] sm:text-xl">
                Cada vez más gente le pide a ChatGPT, Gemini o Perplexity que le recomiende dónde ir. Descubre si la
                IA te nombra a ti <span className="text-[var(--lp-text)]">o a tu competencia</span>, y qué hacer para
                que te recomiende. Pensado para el pequeño negocio, no para grandes marcas.
              </p>
              <div id="comprobar" className="mt-14 scroll-mt-8">
                <CheckForm onSubmit={handleCheck} isLoading={status === 'loading'} />
              </div>
            </div>
            <div className="self-start lg:sticky lg:top-10 lg:col-span-5 lg:pt-10">
              <HeroDemo />
            </div>
          </div>
        </section>

        {/* Check progress / result */}
        {status !== 'idle' && (
          <section ref={resultRef} className="scroll-mt-4 border-t border-[var(--lp-line)]" aria-label="Resultado">
            <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
              {status === 'loading' && <CheckProgress input={input} />}
              {status === 'done' && result && (
                <>
                  {paymentCancelled && <CancelledNote>Tu resultado sigue aquí, por si quieres volver a intentarlo.</CancelledNote>}
                  <CheckResult result={result} input={input} onReset={handleReset} />
                </>
              )}
              {status === 'error' && (
                <div role="alert">
                  <p className="lp-kicker">Algo ha fallado</p>
                  <h2 className="lp-display mt-5 text-[clamp(2.4rem,6vw,4.5rem)]">No hemos podido preguntar.</h2>
                  <p className="mt-6 max-w-xl text-lg text-[var(--lp-muted)]">{errorMessage}</p>
                  <button type="button" onClick={() => handleCheck(input)} className="lp-btn mt-10">
                    Reintentar
                    <ArrowRight size={20} strokeWidth={2.6} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Question marquee */}
        <div aria-hidden="true" className="overflow-hidden border-y border-[var(--lp-line)] py-6">
          <div className="lp-marquee-track">
            {[...MARQUEE_QUESTIONS, ...MARQUEE_QUESTIONS].map((question, i) => (
              <span key={i} className="flex items-center whitespace-nowrap px-6 text-2xl font-semibold tracking-[-0.02em] sm:text-3xl">
                {question}
                <span className="ml-12 text-[var(--lp-lime)]">✳</span>
              </span>
            ))}
          </div>
        </div>

        {/* How it works */}
        <section id="como-funciona" className="mx-auto max-w-6xl scroll-mt-8 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <p className="lp-kicker">Cómo funciona</p>
          <h2 className="lp-display mt-5 max-w-3xl text-[clamp(2.4rem,5.5vw,4.5rem)]">
            Lo que la IA dice de ti, <span className="lp-serif text-[var(--lp-lime)]">sin filtros.</span>
          </h2>
          <ol className="mt-16 grid gap-12 md:grid-cols-3 md:gap-8">
            {STEPS.map((step, i) => (
              <li key={step.title} className="border-t border-[var(--lp-line)] pt-8">
                <span className="lp-display lp-outline block text-7xl">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mt-6 text-xl font-semibold tracking-[-0.02em]">{step.title}</h3>
                <p className="mt-3 leading-relaxed text-[var(--lp-muted)]">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Pricing */}
        <section id="precio" className="mx-auto max-w-6xl scroll-mt-8 px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
          <p className="lp-kicker">Precio</p>
          <h2 className="lp-display mt-5 max-w-3xl text-[clamp(2.4rem,5.5vw,4.5rem)]">
            Empieza gratis. <span className="lp-serif text-[var(--lp-lime)]">Profundiza por {FULL_REPORT_PRICE}.</span>
          </h2>
          <div className="mt-16 grid gap-6 md:grid-cols-2">
            <div className="rounded-[28px] border border-[var(--lp-line)] p-8 sm:p-10">
              <div className="flex items-baseline justify-between">
                <h3 className="text-xl font-semibold">Comprobación</h3>
                <span className="lp-display text-5xl">Gratis</span>
              </div>
              <ul className="mt-10 space-y-4">
                {FREE_ITEMS.map((item) => (
                  <li key={item} className="flex gap-3 text-[var(--lp-muted)]">
                    <ArrowRight size={18} className="mt-1 shrink-0 text-[var(--lp-text)]" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <a href="#comprobar" className="mt-10 inline-flex items-center gap-2 font-semibold underline decoration-[var(--lp-lime)] decoration-2 underline-offset-8">
                Comprobar ahora
              </a>
            </div>

            <div className="rounded-[28px] bg-[var(--lp-lime)] p-8 text-[var(--lp-ink)] sm:p-10">
              <div className="flex items-baseline justify-between">
                <h3 className="text-xl font-semibold">Informe completo</h3>
                <span className="text-right">
                  <span className="lp-display block text-5xl">{FULL_REPORT_PRICE}</span>
                  <span className="lp-mono mt-2 block text-[11px] uppercase tracking-[0.14em] text-black/55">
                    {FULL_REPORT_TAX_NOTE}
                  </span>
                </span>
              </div>
              <ul className="mt-10 space-y-4">
                {FULL_ITEMS.map((item) => (
                  <li key={item} className="flex gap-3 text-black/75">
                    <ArrowRight size={18} className="mt-1 shrink-0" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/informe/ejemplo"
                className="mt-10 inline-flex items-center gap-2 font-semibold underline decoration-2 underline-offset-8"
              >
                Ver un informe de ejemplo
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <p className="mt-4 text-sm text-black/60">Primero haz la comprobación gratuita; el informe se pide desde tu resultado.</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="preguntas" className="mx-auto max-w-4xl scroll-mt-8 px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
          <p className="lp-kicker">Preguntas</p>
          <div className="mt-10 border-t border-[var(--lp-line)]">
            {FAQS.map((faq) => (
              <details key={faq.q} className="lp-faq border-b border-[var(--lp-line)]">
                <summary className="flex items-center justify-between gap-6 py-6 text-lg font-semibold tracking-[-0.01em] sm:text-xl">
                  {faq.q}
                  <Plus size={22} className="lp-faq-icon shrink-0 text-[var(--lp-lime)] transition-transform" aria-hidden="true" />
                </summary>
                <p className="max-w-3xl pb-7 leading-relaxed text-[var(--lp-muted)]">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-[var(--lp-lime)] text-[var(--lp-ink)]">
          <div className="mx-auto flex max-w-6xl flex-col gap-10 px-4 py-20 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8 lg:py-28">
            <h2 className="lp-display text-[clamp(3rem,9vw,8rem)]">
              ¿Te nombra
              <br />
              <span className="lp-serif">la IA?</span>
            </h2>
            <a href="#comprobar" className="lp-btn lp-btn--ink self-start lg:self-auto">
              Compruébalo gratis
              <ArrowRight size={20} strokeWidth={2.6} aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <footer className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-10 lp-mono text-[11px] uppercase tracking-[0.14em] text-[var(--lp-muted)] sm:flex-row sm:justify-between sm:px-6 lg:px-8">
        <span>© {new Date().getFullYear()} webspector · GEO para negocios locales</span>
        <LegalLinks />
      </footer>
    </div>
  );
};

export default Landing;
